/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Where a value on the screen came from** — the one projection every editing surface renders
 * inheritance through (`P3a`, S11).
 *
 * Same rule as `isNavItemVisible`, `readiness.ts` and `artifactIdentityChips`, and the same reason:
 * a surface must not be able to say something another surface hides. Two editors deriving "is this
 * inherited?" separately would eventually disagree, and the one an operator is looking at would be
 * the one that is wrong.
 *
 * ## What it is answering, and why it needs a projection at all
 *
 * A parented artifact is **resolved before anything outside storage sees it** — that is what stops a
 * consumer sending a demand source with no endpoint. The cost is that the document the editor renders
 * says nothing about where each value came from: the parent's endpoint arrives in a box the operator
 * can type into, indistinguishable from one they wrote.
 *
 * `inherited` and `overridden` have been in the console's provenance vocabulary since R4 and were
 * **written by nothing** — an offered name with no writer, one layer out from the macro case. This is
 * the writer.
 *
 * ## A field is often not a leaf
 *
 * `endpoint` is one leaf; `params` is a subtree of dozens. So every question here is asked about a
 * **path prefix**, and a subtree that states some of its leaves and inherits the rest answers
 * `overridden` — with {@link ArtifactPathInheritance.overriddenLeaves} carrying which ones, so a
 * surface can say *how many* rather than implying *all*. The alternative was a fourth chip for
 * "mixed", and a vocabulary that grows a member per aggregation granularity stops being a
 * vocabulary.
 */

import type { ArtifactInheritance } from '../types';

/**
 * What a path is, relative to this artifact's parent.
 *
 * `own` covers the two cases a chip must **not** be rendered for — an artifact with no parent, and a
 * path neither list mentions (a value nothing has stated anywhere, which is most of an empty form).
 * Chipping those is how a provenance system dies: mark everything and the marks stop being read.
 */
export type ArtifactPathState = 'own' | 'inherited' | 'overridden';

export interface ArtifactPathInheritance {
  state: ArtifactPathState;
  /**
   * The leaves under this path that the artifact states for itself — **exactly what a revert here
   * would give back to the parent**, and nothing else. Empty unless `state` is `overridden`.
   */
  overriddenLeaves: string[];
}

const NOTHING: ArtifactPathInheritance = { state: 'own', overriddenLeaves: [] };

/**
 * Whether `path` is `prefix`, or sits underneath it.
 *
 * Segment-aware rather than a `startsWith`: `regs` must not match `regsExt`, and matching on
 * characters instead of segments is the same defect as matching a macro against part of a word.
 */
function isUnder(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}.`);
}

/**
 * Resolve one field's inheritance.
 *
 * `inheritance` is nullable throughout because the query is one the page can be rendering without:
 * the answer while it loads, and for a type whose store cannot answer, is *nothing to say* — never a
 * spinner beside every field, and never a guess.
 */
export function pathInheritance(
  inheritance: ArtifactInheritance | null | undefined,
  path: string,
): ArtifactPathInheritance {
  if (!inheritance?.parentId) return NOTHING;

  const overriddenLeaves = inheritance.overriddenPaths.filter((p) => isUnder(p, path));
  const inheritsHere = inheritance.inheritedPaths.some((p) => isUnder(p, path));
  if (overriddenLeaves.length > 0) {
    // **What this artifact added replaces nothing, so it is not `overridden`** (`#1126`) — it is its
    // own, unchipped, the design system's rule that an unchipped value is plainly the author's, and
    // it offers no revert, because reverting what the parent does not have deletes it.
    //
    // **Added is decided for the whole path, never leaf by leaf.** A leaf the parent lacks, inside a
    // row the parent has, is an override *of that row*: a suppression the parent never stated, or a
    // default under a spelling the parent's pre-`#1216` document does not use. Measured on a real
    // child (`demo-override-test`, 2026-09-18) — dropping added leaves one at a time left both rows
    // reading `inherited`, with no way back.
    const added = new Set(inheritance.addedPaths ?? []);
    if (!inheritsHere && overriddenLeaves.every((p) => added.has(p))) return NOTHING;
    return { state: 'overridden', overriddenLeaves };
  }
  if (inheritsHere) return { state: 'inherited', overriddenLeaves: [] };
  return NOTHING;
}

/**
 * One answer across several paths — **for a header that stands over more than one subtree.**
 *
 * The HTTP section holds `headers` and `params`, and rendering a chip per subtree put *inherited*
 * twice in one header, which reads as a repeat rather than as two facts (Todd, 2026-08-24). The
 * header now says one thing about the section and the subtrees say their own when it is open.
 *
 * `overridden` wins across the set, and the leaves are the **union** — so a revert from the header
 * gives back everything the section overrides, which is what a single control over a whole section
 * has to mean.
 */
export function pathsInheritance(
  inheritance: ArtifactInheritance | null | undefined,
  paths: readonly string[],
): ArtifactPathInheritance {
  const answers = paths.map((path) => pathInheritance(inheritance, path));
  const overriddenLeaves = [...new Set(answers.flatMap((a) => a.overriddenLeaves))];
  if (overriddenLeaves.length > 0) return { state: 'overridden', overriddenLeaves };
  if (answers.some((a) => a.state === 'inherited')) return { state: 'inherited', overriddenLeaves: [] };
  return NOTHING;
}

/**
 * The provenance chip for a path, or `undefined` where there is nothing to say.
 *
 * Returns the shared `ValueProvenance` members rather than a label, so the chip's text and treatment
 * keep coming from `provenance-presentation.ts` — the surface that renders it never gets to phrase
 * it, which is what stops a page shortening "overridden here" and disagreeing with its own legend.
 */
export function inheritanceProvenance(
  inheritance: ArtifactInheritance | null | undefined,
  path: string,
): 'inherited' | 'overridden' | undefined {
  const { state } = pathInheritance(inheritance, path);
  return state === 'own' ? undefined : state;
}

/**
 * How to describe reverting this path, for the control that offers it.
 *
 * Says what the operator gets back, not what the mechanism does — *"take the parent's value"* rather
 * than *"delete from layer"*. The count is stated whenever it is more than one, because a section's
 * chip says `overridden here` for a subtree that may be one row out of forty.
 */
export function describeRevert(target: ArtifactPathInheritance, parentName?: string): string | undefined {
  const count = target.overriddenLeaves.length;
  if (count === 0) return undefined;
  const from = parentName ? ` from ${parentName}` : ' from its parent';
  // Named for no type: the same mark stands on a demand source and a request strategy (slice 7a).
  return count === 1 ? `Use the value inherited${from}.` : `Use the ${count} values inherited${from}.`;
}

/**
 * The layer paths one editor row could occupy.
 *
 * **Derived here rather than in each editor**, for the reason the whole module exists: a path built
 * in the parameter editor and a path built in the header editor would answer the same question two
 * ways, and the one that drifts is the one somebody is looking at. It is also the part most likely
 * to be quietly wrong — a chip is rendered either way, and a mark against a path nothing states just
 * silently does not appear.
 *
 * **One store per concept.** A request parameter lives at `params.fields.<name>`, and driver
 * configuration (`version`, `timeout`) on its own fields — `params.staticParams` is retired
 * (`#1135`). This briefly returned both paths,
 * so a row authored before `rowsToParamsDto` became canonical would still be marked — and Todd's
 * ruling on it is the pre-1.0 policy applied where it was being quietly skipped:
 *
 * > *"this is another place we're adding work, creating bugs for the goal of backwards
 * > compatibility, which isn't a goal yet. While demand-sources are the one place where there is
 * > real user-data to migrate, better now while there are few."*
 *
 * The data moved instead — `scripts/repair-demand-source-shape.ts` **R5** — so this asks one store,
 * and the second one cannot come back without something failing.
 *
 * It still returns a list because a header and a parameter answer the same call, and a surface that
 * genuinely has two homes for one concept should be fixed rather than accommodated here.
 */
export function paramRowPaths(name: string): string[] {
  const key = name.trim();
  return key ? [`params.fields.${key}`] : [];
}

/** The layer path a header row occupies. One store, so one candidate. */
export function headerRowPaths(key: string): string[] {
  const name = key.trim();
  return name ? [`headers.${name}`] : [];
}

/**
 * The layer path an **ORTB body leaf** occupies — or none, when the layer cannot address it.
 *
 * The body is stored at `ortbBodyTemplate.body`, so a leaf at `['device','w']` is
 * `ortbBodyTemplate.body.device.w` and is addressed exactly.
 *
 * **A leaf inside an array is not addressable, and this returns nothing rather than a path that
 * quietly matches nothing** (#1143). `layerFor` merges objects key by key and replaces arrays
 * wholesale, so a child that changes one field inside `imp[0]` stores the **entire** `imp` array —
 * measured, not read: the layer's leaf paths come back as `ortbBodyTemplate.body.imp`, one entry for
 * all of it. `pathInheritance` matches a stated path at or *under* the query, so asking about
 * `…body.imp.0.video.w` finds nothing at all, and a chip that renders nothing is indistinguishable
 * from a field with no opinion.
 *
 * So the array case is answered at the **section**, by {@link ortbBodySectionPath}, where the
 * statement is true — and the limit is stated in the product rather than implied by an absent chip.
 * Per-leaf addressing inside an array is `P3b`/#918: the array has to become path-keyed first.
 *
 * **`imp` became path-keyed on 2026-09-18** (RS4c, slice `1d`): it is authored as a pattern node,
 * `imp.$each.*` and `imp.$at.<key>.*`, which is an object the layer merges key by key — so its leaves
 * carry no numeric segment and are addressed here like any other. A literal array (a replace body's
 * own `imp`) is still answered at the section.
 */
export function ortbBodyLeafPaths(path: ReadonlyArray<string | number>): string[] {
  if (path.length === 0) return [];
  if (path.some((segment) => typeof segment === 'number')) return [];
  return [`ortbBodyTemplate.body.${path.join('.')}`];
}

/**
 * **The body leaves this source removes from its parent, by label** (`#1379`) — `user.consent` for a
 * stored `ortbBodyTemplate.body.user.consent`. Any depth, unlike `removedNamesUnder`, because a body
 * leaf's label *is* its dotted path; never inside an array, which is not layered per element.
 */
export function removedBodyLabels(removedFromParent: readonly string[] | undefined): string[] {
  const head = 'ortbBodyTemplate.body.';
  return (removedFromParent ?? []).filter((p) => p.startsWith(head)).map((p) => p.slice(head.length));
}

/** The layer path a body leaf's label is removed at — the inverse of {@link removedBodyLabels}. */
export function bodyRemovalPath(label: string): string {
  return `ortbBodyTemplate.body.${label}`;
}

/** The layer path an ORTB body **section** occupies — `imp`, `device`, `regs`, … */
export function ortbBodySectionPath(section: string): string {
  return `ortbBodyTemplate.body.${section}`;
}

/**
 * Is this section stored as **one leaf**, so nothing inside it can be marked individually?
 *
 * True exactly when the layer's answer for the section is the section itself rather than paths
 * beneath it — which is what an array produces. Asked of the *stored* answer rather than of a list
 * of section names, so a body shape nobody anticipated is described rather than assumed.
 */
export function isStatedAsWhole(inheritance: ArtifactInheritance | null | undefined, path: string): boolean {
  if (!inheritance?.parentId) return false;
  return [...inheritance.overriddenPaths, ...inheritance.inheritedPaths].includes(path);
}

/** {@link isStatedAsWhole} for an ORTB body section. */
export function isWholeSectionLeaf(inheritance: ArtifactInheritance | null | undefined, section: string): boolean {
  return isStatedAsWhole(inheritance, ortbBodySectionPath(section));
}

/**
 * The chip for a row, given every path it could occupy.
 *
 * `overridden` is checked across the whole candidate set rather than per path: a row stated at *any*
 * of its paths is a row this artifact states, and answering from the first path alone would report
 * an override as inherited whenever the earlier path had nothing to say.
 */
export function rowInheritanceProvenance(
  inheritance: ArtifactInheritance | null | undefined,
  paths: readonly string[],
): 'inherited' | 'overridden' | undefined {
  const answers = paths.map((path) => pathInheritance(inheritance, path).state);
  if (answers.includes('overridden')) return 'overridden';
  if (answers.includes('inherited')) return 'inherited';
  return undefined;
}

/**
 * **Does this draft override something the stored artifact inherits?** — the section-level half of
 * `draftRowInheritance`, for a heading shown over an open editor.
 *
 * The heading read only what is stored, so it said `inherited` above a row saying `overridden here`
 * (Todd, 2026-09-23). A draft overrides inherited content when an inherited row changed, went missing,
 * or was removed from the parent in this draft. A row the artifact added itself overrides nothing.
 */
export function draftOverridesInherited<T>(
  inheritance: ArtifactInheritance | null | undefined,
  {
    draft,
    stored,
    keyOf,
    pathsOf,
    removedInDraft,
  }: {
    draft: readonly T[];
    stored: readonly T[];
    keyOf: (row: T) => string;
    pathsOf: (key: string) => string[];
    /** Names removed from the parent in this draft and not in what is stored. */
    removedInDraft: readonly string[];
  },
): boolean {
  const inherited = (key: string) => rowInheritanceProvenance(inheritance, pathsOf(key)) === 'inherited';
  if (removedInDraft.some(inherited)) return true;
  const drafted = new Map(draft.map((row) => [keyOf(row), row]));
  return stored.some((row) => {
    if (!inherited(keyOf(row))) return false;
    const now = drafted.get(keyOf(row));
    return now === undefined || JSON.stringify(withoutRowIdentity(now)) !== JSON.stringify(withoutRowIdentity(row));
  });
}

/** A row's content, without the id a projection mints and the authoring provenance beside it. */
function withoutRowIdentity<T>(row: T): unknown {
  if (row === null || typeof row !== 'object') return row;
  const { id: _id, provenance: _provenance, ...rest } = row as Record<string, unknown>;
  return rest;
}

/**
 * **The mark for a row the operator is currently editing** (found 2026-08-24 in validation).
 *
 * `inheritance` is a fact about the **stored** artifact — the server resolved it, and it does not
 * move while a draft is open. So a row the operator has just typed into went on rendering
 * `inherited`, and only became `overridden here` after saving and refetching. The value on the
 * screen was theirs and the label said it was the parent's.
 *
 * That is the resolution-mark rule one surface over: *state what is in force, or say you do not
 * know* — and a false positive is worse than an omission, because an omission reads as a problem
 * and a value reads as proof. Here the false positive is the more dangerous direction on its own
 * terms: `inherited` tells the operator *this will follow the parent*, about a value that no longer
 * will.
 *
 * **It is optimistic, and deliberately so.** The browser cannot ask what the parent holds — a
 * viewer may hold a child whose parent they may not see, which is why revert takes paths and never
 * values (`P3a`). So this reasons from the only thing it can see: the operator has changed this
 * row, therefore the row is no longer the parent's. It is wrong in exactly one case — typing the
 * parent's value back character for character, where the save will store nothing and the row
 * returns to `inherited` — and that self-corrects on save, in the direction that does not tell
 * anyone a change is safe.
 *
 * **A row with nothing to say stays silent.** If the stored projection had no opinion (no parent,
 * or a path neither list mentions) then editing it does not create one. Chipping a row because it
 * was touched is how a provenance system dies.
 */
export function draftRowInheritance(
  inheritance: ArtifactInheritance | null | undefined,
  paths: readonly string[],
  changed: boolean,
): 'inherited' | 'overridden' | undefined {
  const stored = rowInheritanceProvenance(inheritance, paths);
  if (!changed || stored === undefined) return stored;
  return 'overridden';
}

/**
 * Has this row diverged from the one that is stored?
 *
 * `id` is excluded because it is **identity, not content**, and it is minted fresh on every
 * projection — `paramsDtoToRows` and `createParameterRow` both generate one, so the stored row and
 * the draft built from it never share theirs. Comparing it would report every row as changed, which
 * is the same defect as reporting none.
 *
 * `provenance` is excluded because it is **authoring metadata, not the value** — the ORTB body's
 * own comparison already said so. It became load-bearing with `#1001`: opening a detected row and
 * pressing Apply clears its guess, and counting that as a change would mark an unedited inherited row
 * *overridden here*, which is a claim about a value nobody touched.
 */
export function rowChangedFromStored<T extends { id: string; provenance?: unknown }>(
  stored: T | undefined,
  draft: T,
): boolean {
  if (!stored) return false;
  const { id: _storedId, provenance: _storedOrigin, ...before } = stored;
  const { id: _draftId, provenance: _draftOrigin, ...after } = draft;
  return JSON.stringify(before) !== JSON.stringify(after);
}

/**
 * **The draft with one row put back as stored** — the draft-only revert (`InheritanceMark.restoreDraft`).
 * The row keeps its draft `id`, because ids are minted per projection and the editor tracks the row
 * by it (`rowChangedFromStored`).
 */
export function rowsWithStoredRow<T extends { id: string }>(rows: readonly T[], target: T, stored: T): T[] {
  return rows.map((row) => (row.id === target.id ? { ...stored, id: row.id } : row));
}

/**
 * **What a row's one action is, on the layer it sits in** (`#1126`, `X31`, reworked 2026-09-18).
 *
 * *Delete* means *stop overriding* against a parent: deleting a row the parent has gives the parent's
 * row back. So a bare `✕` that removes is honest only on a row the parent does not have:
 *
 * | The row | Action |
 * | --- | --- |
 * | on a standalone artifact, or added here | `remove` — the `✕` removes it |
 * | the parent's, **standing in for a field the system sends automatically** | `stop-overriding` — the `✕` removes the parent's row *from this source*, and the automatic value goes out instead |
 * | any other row of the parent's | `null` — no row action |
 *
 * **The first build offered *Suppress here* and *Send it again* on the parent's rows, and Todd
 * turned both down in rehearsal:** *"the user can do that by editing the row, this is only a
 * short-cut that clutters the screen."* Suppressing is the row's own *Never send it*, and giving a
 * row back is the inheritance mark's revert, so a parent's row that displaces nothing has no action
 * the row does not already have.
 *
 * The case he did want is the middle one: *"a parent overrides a sent-automatically field, and the
 * author wants to stop overriding. That's not a suppress — it's overriding the parent's override."*
 * `suppress` cannot say it (a suppressed row still claims the field, so the automatic value stays
 * off), and absence gives the parent's row back, so it is stored as a **removal** — a `null` in this
 * source's layer (`artifact-layer.ts`), carried on the document as `removedFromParent`.
 *
 * **Which rows displace an automatic field is the surface's declaration**, never a guess here: the
 * parameter set is `displacedAutoParam`, the header set `driverHeaderField` (`4b-ii`, act
 * since `#1422`). A surface with no such declaration answers `false` for every row, and its parent
 * rows then have no action. The act itself is shared: `parent-removals.tsx`.
 */
export type RowLayerActionKind = 'remove' | 'stop-overriding';

export function rowLayerActionKind(
  inheritance: ArtifactInheritance | null | undefined,
  paths: readonly string[],
  displacesAutomatic: boolean,
): RowLayerActionKind | null {
  if (rowInheritanceProvenance(inheritance, paths) === undefined) return 'remove';
  return displacesAutomatic ? 'stop-overriding' : null;
}

/**
 * The names a source removes from its parent under one keyed subtree — `params.fields` gives the
 * parameter names (`#1126`). Only the direct children: a removal deeper than a row is not a row
 * removal, and no surface authors one.
 */
export function removedNamesUnder(removedFromParent: readonly string[] | undefined, prefix: string): string[] {
  const head = `${prefix}.`;
  return (removedFromParent ?? [])
    .filter((p) => p.startsWith(head) && !p.slice(head.length).includes('.'))
    .map((p) => p.slice(head.length));
}
